/**
 * =============================================================================
 * Componente: PlaybookHelpDialog
 * Versão: 1.1.0
 *
 * Descrição:
 *   Modal de ajuda do editor de Playbook de Vendas. Explica de forma didática:
 *     - O que é um Playbook e como funciona
 *     - Os dois modos (flat vs per_phase)
 *     - As origens de prazo disponíveis (contextual por modo) com 3 exemplos
 *     - Como o sistema cria steps ao mover o deal entre fases
 *     - Comportamento de conclusão e reabertura de cadência
 *
 * Acionado por um botão "?" no topo da página de edição do playbook.
 *
 * Release notes:
 *   v1.1.0 - Termo "anchor" substituído por "origem do prazo". Cards agora
 *            indicam em qual modo (flat / per_phase) cada opção aparece, para
 *            evitar a confusão de "o modal mostra 4 e a UI só tem 2".
 *   v1.0.0 - Criação (ajuda contextual sobre regras do playbook).
 * =============================================================================
 */
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Flag,
  Link2,
  CalendarPlus,
  LogIn,
  Layers,
  Workflow,
  Info,
} from 'lucide-react';

interface PlaybookHelpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ----------------------------------------------------------------------------
// Subcomponente: bloco de seção
// ----------------------------------------------------------------------------
function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </div>
        <h3 className="text-base font-semibold">{title}</h3>
      </div>
      <div className="text-sm text-muted-foreground space-y-2 pl-9">
        {children}
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Subcomponente: card explicando um anchor
// ----------------------------------------------------------------------------
function AnchorCard({
  icon: Icon,
  badge,
  title,
  marco,
  examples,
  availableIn,
}: {
  icon: React.ComponentType<{ className?: string }>;
  badge: string;
  title: string;
  marco: string;
  examples: string[];
  /** Em quais modos do playbook esta origem de prazo aparece no select da UI. */
  availableIn: 'flat' | 'per_phase' | 'ambos';
}) {
  // Rótulo amigável para o usuário entender onde a opção aparece
  const availabilityLabel =
    availableIn === 'flat'
      ? 'Disponível em: Lista única'
      : availableIn === 'per_phase'
        ? 'Disponível em: Passos por fase'
        : 'Disponível em: ambos os modos';

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold">{title}</h4>
            <Badge variant="outline" className="font-mono text-[10px]">
              {badge}
            </Badge>
            <Badge variant="secondary" className="text-[10px]">
              {availabilityLabel}
            </Badge>
          </div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Marco zero:</span> {marco}
      </p>
      <div className="space-y-1">
        <p className="text-xs font-medium text-foreground">Exemplos práticos:</p>
        <ol className="text-xs text-muted-foreground space-y-1 list-decimal pl-4">
          {examples.map((ex, i) => (
            <li key={i}>{ex}</li>
          ))}
        </ol>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Componente principal
// ----------------------------------------------------------------------------
export function PlaybookHelpDialog({ open, onOpenChange }: PlaybookHelpDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90dvh] p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Info className="h-5 w-5 text-primary" />
            Como funciona o Playbook de Vendas
          </DialogTitle>
          <DialogDescription>
            Guia rápido das regras, modos de operação e da origem do prazo de
            cada passo.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90dvh-110px)]">
          <div className="px-6 py-5 space-y-6">
            {/* ---------------------------------------------------------------- */}
            {/* O que é                                                          */}
            {/* ---------------------------------------------------------------- */}
            <Section icon={Workflow} title="O que é um Playbook">
              <p>
                Um Playbook é uma sequência de passos (ligar, mandar WhatsApp,
                criar lembrete etc.) que o sistema cria automaticamente para o
                vendedor seguir em cada negócio. Cada passo tem um{' '}
                <span className="font-medium text-foreground">prazo</span>{' '}
                (due_at) que aparece para o vendedor como “fazer até tal data”.
              </p>
              <p>
                Os passos são executados na ordem definida. O vendedor vai
                marcando como concluído conforme avança, e o sistema acompanha o
                progresso da cadência no negócio.
              </p>
            </Section>

            {/* ---------------------------------------------------------------- */}
            {/* Modos                                                            */}
            {/* ---------------------------------------------------------------- */}
            <Section icon={Layers} title="Modos de organização dos passos">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-lg border bg-card p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="secondary" className="font-mono text-[10px]">
                      flat
                    </Badge>
                    <span className="text-sm font-medium text-foreground">
                      Lista única
                    </span>
                  </div>
                  <p className="text-xs">
                    Todos os passos numa única sequência, sem vínculo com
                    fases. Ideal para cadências de prospecção, nutrição ou
                    onboarding.
                  </p>
                </div>
                <div className="rounded-lg border bg-card p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="secondary" className="font-mono text-[10px]">
                      per_phase
                    </Badge>
                    <span className="text-sm font-medium text-foreground">
                      Passos por fase
                    </span>
                  </div>
                  <p className="text-xs">
                    Os passos são agrupados por fase do pipeline. Quando o
                    negócio entra numa fase, o sistema injeta automaticamente
                    os passos daquela fase. Recomendado para playbooks
                    comerciais completos.
                  </p>
                </div>
              </div>
            </Section>

            {/* ---------------------------------------------------------------- */}
            {/* Origens de prazo                                                 */}
            {/* ---------------------------------------------------------------- */}
            <Section icon={Flag} title="Origens de prazo disponíveis">
              <p>
                A <span className="font-medium text-foreground">origem do prazo</span>{' '}
                define <span className="font-medium text-foreground">a partir de qual momento</span>{' '}
                o sistema conta o prazo do passo. Ela não controla a ordem
                (isso é o número do passo) — controla{' '}
                <span className="font-medium text-foreground">quando o relógio começa</span>.
              </p>
              <p className="text-xs italic">
                Ex: passo com prazo de “2 dias”. A origem do prazo responde: 2 dias{' '}
                <span className="font-medium not-italic text-foreground">contados a partir de quando?</span>
              </p>

              {/* Aviso de contexto: a lista exibida no select depende do modo */}
              <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1">
                <p className="font-medium text-foreground">
                  Quais opções aparecem na configuração de cada passo?
                </p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>
                    Modo <span className="font-mono">flat</span> (Lista única) —
                    mostra <span className="font-medium text-foreground">3 opções</span>:
                    Início da cadência, Após passo anterior e Criação do negócio.
                  </li>
                  <li>
                    Modo <span className="font-mono">per_phase</span> (Passos por fase) —
                    mostra <span className="font-medium text-foreground">2 opções</span>:
                    Entrada na fase e Após passo anterior.
                  </li>
                </ul>
                <p className="text-muted-foreground pt-1">
                  Por isso, ao editar passos de uma fase você só vê 2 opções no
                  campo — as demais não fazem sentido neste modo.
                </p>
              </div>

              <div className="grid gap-3 pt-1">
                <AnchorCard
                  icon={Flag}
                  badge="cadence_start"
                  title="Início da cadência"
                  availableIn="flat"
                  marco="O instante em que a cadência foi disparada para o negócio (entrada no playbook)."
                  examples={[
                    'Onboarding: passo 1 “Ligar” em D+0, passo 2 “Mandar proposta” em D+2, passo 3 “Follow-up” em D+5 — cronograma absoluto, mesmo que o vendedor adiantou ou atrasou um passo.',
                    'Campanha de nutrição de 30 dias com e-mails programados em D+1, D+7, D+15 e D+30 a partir do início da cadência.',
                    'SLA de primeiro contato: “primeira ligação em até 1h após a cadência iniciar”.',
                  ]}
                />

                <AnchorCard
                  icon={Link2}
                  badge="previous_step_completed"
                  title="Após passo anterior"
                  availableIn="ambos"
                  marco="O instante em que o passo imediatamente anterior foi marcado como concluído."
                  examples={[
                    'Após “Enviar proposta”, criar passo “Ligar para confirmar recebimento” 1 dia depois — o relógio só começa quando a proposta foi de fato enviada.',
                    'Após “Reunião realizada”, criar passo “Enviar ata” 2h depois e “Cobrar feedback” 2 dias depois da ata.',
                    'Após “Mandar contrato”, criar passo “Cobrar assinatura” 3 dias depois do envio.',
                  ]}
                />

                <AnchorCard
                  icon={CalendarPlus}
                  badge="deal_created"
                  title="Criação do negócio"
                  availableIn="flat"
                  marco="A data de criação do deal (crm_deals.created_at), independe da cadência ou da fase."
                  examples={[
                    'SLA comercial duro: “todo deal precisa ter proposta enviada em até 7 dias da criação”, mesmo que o playbook tenha começado depois.',
                    'Régua de qualificação: “lead precisa estar qualificado em 3 dias desde a criação do deal”.',
                    'Alerta de envelhecimento: passo “Revisar deal parado” com prazo 30 dias após a criação do deal.',
                  ]}
                />

                <AnchorCard
                  icon={LogIn}
                  badge="phase_entered"
                  title="Entrada na fase"
                  availableIn="per_phase"
                  marco="O instante em que o negócio entrou na fase atual. Exclusivo do modo per_phase."
                  examples={[
                    'Fase “Enviar proposta”: passo “Enviar proposta” em D+0 da entrada e passo “Cobrar retorno” 2 dias após entrar na fase.',
                    'Fase “Negociação”: passo “Reunião de fechamento” 3 dias após o deal cair em negociação.',
                    'Fase “Pós-venda”: passo “Ligar para boas-vindas” 1 dia após o deal entrar em pós-venda.',
                  ]}
                />
              </div>

              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs">
                <span className="font-medium text-foreground">⚠️ Atenção:</span>{' '}
                Em playbooks <span className="font-mono">per_phase</span>, prefira{' '}
                <span className="font-mono">phase_entered</span> e{' '}
                <span className="font-mono">previous_step_completed</span>. A
                origem <span className="font-mono">deal_created</span> em deals
                antigos pode gerar prazos já vencidos.
              </div>
            </Section>

            {/* ---------------------------------------------------------------- */}
            {/* Comportamento ao mudar de fase                                   */}
            {/* ---------------------------------------------------------------- */}
            <Section icon={Workflow} title="Como o sistema cria passos ao mudar de fase">
              <ol className="list-decimal pl-4 space-y-1.5">
                <li>
                  Quando o negócio entra numa fase, o sistema busca os passos
                  cadastrados para aquela fase no playbook vinculado.
                </li>
                <li>
                  Os passos são criados na cadência do deal com o{' '}
                  <span className="font-medium text-foreground">due_at</span>{' '}
                  calculado conforme o anchor de cada passo.
                </li>
                <li>
                  Em <span className="font-mono">per_phase</span>, a cadência
                  fica <span className="font-medium text-foreground">ativa</span>{' '}
                  enquanto houver fases futuras com passos cadastrados — mesmo
                  que todos os passos da fase atual já tenham sido concluídos.
                </li>
                <li>
                  Se a cadência foi fechada antes do tempo (legado) e o deal
                  entra numa fase com passos novos, o sistema{' '}
                  <span className="font-medium text-foreground">reabre</span> a
                  cadência automaticamente e injeta os passos.
                </li>
                <li>
                  A cadência só é finalizada quando não há mais passos pendentes
                  em fases futuras (ou o deal foi ganho/perdido).
                </li>
              </ol>
            </Section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
